import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AuthenticationService } from '../../../services/authentication.service';
import { InputComponent } from '@shared/components/input/input.component';
import { BUTTON_TYPES } from '@shared/components/button/button-types.enum';
import { matchValidator } from '../validators.const';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PasswordValidationComponent } from '@shared/components/password-validation/password-validation.component';
import { PASSWORD_REQUIREMENTS_RESET } from '../signup/signup.const';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ToasterType } from '@shared/components/message-toaster/message-toaster.component';
import { ButtonComponent } from '@shared/components/button/button.component';

export interface IPasswordForm {
  confirmPassword: FormControl<string | null>;
  password: FormControl<string | null>;
}


@UntilDestroy()
@Component({
  selector: 'app-reset-password-modal',
  imports: [
    InputComponent,
    ReactiveFormsModule,
    TranslateModule,
    PasswordValidationComponent,
    ButtonComponent,
  ],
  templateUrl: './reset-password-modal.component.html',
  styleUrl: './reset-password-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResetPasswordModalComponent implements OnInit {
  dialogRef = inject<DialogRef<string>>(DialogRef<string>);
  data = inject(DIALOG_DATA);
  resetPasswordForm!: FormGroup<IPasswordForm>;
  passwordRequirements = [...PASSWORD_REQUIREMENTS_RESET];
  arePasswordRequirementsMet = false;
  isSubmitted = false;
  protected readonly ButtonTypes = BUTTON_TYPES;

  constructor(
    private authService: AuthenticationService,
    private fb: FormBuilder,
    private translate: TranslateService,
  ) {}

  get isSubmitAvailable(): boolean {
    return !!this.resetPasswordForm.value.password && !!this.resetPasswordForm.value.confirmPassword && this.arePasswordRequirementsMet;
  }

  ngOnInit() {
    this.createForm();
  }

  createForm() {
    this.resetPasswordForm = this.fb.group({
      password: new FormControl('', [Validators.required]),
      confirmPassword: new FormControl('', [Validators.required]),
    }, {
      validators: matchValidator('password', 'confirmPassword'),
    });
    this.validatePasswordHint();
  }

  validatePasswordHint() {
    this.resetPasswordForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.passwordRequirements.map(requirement => {
        let regex = new RegExp(requirement.validation);
        requirement.valid = regex.test(String(this.resetPasswordForm.value.password));
      });
      this.arePasswordRequirementsMet = this.passwordRequirements.every(req => req.valid);
    });
  }

  submitPassword(): void {
    this.isSubmitted = true;
    if (this.arePasswordRequirementsMet && this.resetPasswordForm.valid) {
      this.authService.forgotPasswordSubmit(this.data.userEmail, this.data.authorizationCode, String(this.resetPasswordForm.value.password))
        .then(data => {
          const message = this.translate.instant('Your password has been updated successfully');
          this.authService.openMessage(ToasterType.SUCCESS, message);
          this.closeModal();
        }).catch(error => this.authService.openMessage(ToasterType.ERROR, error.message));
    }
  }

  closeModal() {
    this.dialogRef.close();
  }


  protected readonly BUTTON_TYPES = BUTTON_TYPES;
}
